import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

type Step = {
    id: string
    label: string
    eyebrow: string
    headline: string
    description: string
    bullets: string[]
    image: string
}

const steps: Step[] = [
    {
        id: 'hire',
        label: 'Hire',
        eyebrow: 'Step 01',
        headline: 'Pick workers from a 100+ person bench.',
        description:
            'Browse a roster of digital workers organized by department — Sales, Marketing, Operations, Platform. Each has a role, a track record, and live availability.',
        bullets: [
            'Filter by department, role, or capability',
            'See past output and execution history',
            'Hire one or build a full team in minutes',
        ],
        image: '/platform_screenshots/workers.jpg',
    },
    {
        id: 'configure',
        label: 'Configure',
        eyebrow: 'Step 02',
        headline: 'Hand them an offer, a list, or a workflow.',
        description:
            'Give your workers context: who you sell to, what you’re shipping, which tools to use. They build their own playbooks from there.',
        bullets: [
            'Define offers, ICPs, and brand voice once',
            'Connect Gmail, Instagram, HubSpot, Stripe, and more',
            'Drop in workflows on a schedule or trigger',
        ],
        image: '/platform_screenshots/campaigns.jpg',
    },
    {
        id: 'execute',
        label: 'Execute',
        eyebrow: 'Step 03',
        headline: 'They run — in parallel, around the clock.',
        description:
            'Workers execute autonomously across your stack. Conversations get answered. Posts go out. Pipelines move. You stay in the loop without being in the way.',
        bullets: [
            'Multi-worker workflows running concurrently',
            'Real-time activity feed across every department',
            'Auto-pause on anomalies and route to you',
        ],
        image: '/platform_screenshots/operations-kanban.jpg',
    },
    {
        id: 'review',
        label: 'Scale',
        eyebrow: 'Step 04',
        headline: 'Approve the big swings. Scale what works.',
        description:
            'You review high-impact actions, see what each worker shipped, and double down on what’s converting. No timesheets — just outputs and outcomes.',
        bullets: [
            'One-click approvals for key actions',
            'Per-worker output, cost, and conversion reporting',
            'Add capacity by hiring another worker, not another seat',
        ],
        image: '/platform_screenshots/operations-overview.jpg',
    },
]

export function PlatformFlow() {
    const [activeId, setActiveId] = useState<string>(steps[0].id)
    const active = steps.find((s) => s.id === activeId) ?? steps[0]

    return (
        <div className="w-full">
            {/* Tab strip */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="max-w-3xl mx-auto mb-12"
            >
                <div
                    role="tablist"
                    aria-label="Platform workflow steps"
                    className="relative border border-border rounded-full p-1.5 flex flex-wrap gap-1 bg-card/50"
                >
                    {steps.map((step) => {
                        const isActive = step.id === activeId
                        return (
                            <button
                                key={step.id}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                aria-controls={`flow-panel-${step.id}`}
                                id={`flow-tab-${step.id}`}
                                onClick={() => setActiveId(step.id)}
                                className={`relative flex-1 min-w-[120px] px-5 py-2.5 rounded-full font-body text-sm transition-colors duration-300 cursor-pointer ${isActive
                                    ? 'text-foreground'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                {isActive && (
                                    <motion.span
                                        layoutId="flow-tab-pill"
                                        className="absolute inset-0 bg-primary/10 border border-primary/30 rounded-full"
                                        transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                                    />
                                )}
                                <span className="relative z-10 flex items-center justify-center gap-2">
                                    <span className="font-heading text-xs text-muted-foreground">
                                        0{steps.findIndex((s) => s.id === step.id) + 1}
                                    </span>
                                    {step.label}
                                </span>
                            </button>
                        )
                    })}
                </div>
            </motion.div>

            {/* Active panel */}
            <div
                id={`flow-panel-${active.id}`}
                role="tabpanel"
                aria-labelledby={`flow-tab-${active.id}`}
                className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center"
            >
                {/* Copy */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={`${active.id}-copy`}
                        initial={{ opacity: 0, x: -24 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 24 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-2"
                    >
                        <span className="font-heading text-sm text-muted-foreground">
                            {active.eyebrow}
                        </span>
                        <h3 className="font-heading text-3xl md:text-4xl text-foreground mt-2 mb-4 leading-tight">
                            {active.headline}
                        </h3>
                        <p className="font-body text-base text-muted-foreground font-light mb-6">
                            {active.description}
                        </p>
                        <ul className="space-y-3">
                            {active.bullets.map((b) => (
                                <li
                                    key={b}
                                    className="flex items-start gap-3 font-body text-sm text-foreground/90"
                                >
                                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                </AnimatePresence>

                {/* Screenshot */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={`${active.id}-img`}
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.97 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-3 relative rounded-2xl overflow-hidden border border-border bg-card shadow-2xl"
                    >
                        <img
                            src={active.image}
                            alt={`${active.label}: ${active.headline}`}
                            className="w-full h-auto object-cover object-top"
                        />
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    )
}
